import { FormControl, FormLabel, Box } from '@mui/material';
import { FC } from 'react';
import { cn } from '../../../../util';
import { CheckmarkIcon } from '../../../../icons';
import { TooltipComponent } from '../../feedback';

export interface CheckboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CheckboxGroupProps {
  options: CheckboxOption[];
  value: string | undefined;
  onChange: (selectedValue: string | undefined) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  tooltip?: string;
  row?: boolean;
}

export const CheckboxGroup: FC<CheckboxGroupProps> = ({
  options,
  value,
  onChange,
  label,
  error,
  helperText,
  required,
  disabled,
  tooltip,
  row = true
}) => {
  const handleChange = (optionValue: string) => {
    if (value === optionValue) {
      onChange(undefined);
    } else {
      onChange(optionValue);
    }
  };

  return (
    <TooltipComponent title={tooltip} arrow>
      <FormControl error={error} disabled={disabled}>
        {label && (
          <FormLabel required={required} className="mb-2">
            {label}
          </FormLabel>
        )}
        <Box className={cn('flex mt-3', row ? 'flex-row flex-wrap gap-x-4' : 'flex-col gap-y-2')}>
          {options.map((option) => {
            const isChecked = value === option.value;
            const isDisabled = disabled ?? option.disabled;

            return (
              <label
                key={option.value}
                className={cn(
                  'flex items-center cursor-pointer group',
                  isDisabled && 'cursor-not-allowed'
                )}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => !isDisabled && handleChange(option.value)}
                  disabled={isDisabled}
                  className="sr-only" // hide default checkbox
                />

                <div
                  className={cn(
                    'w-4 h-4 rounded-md border-solid border-2 flex items-center justify-center transition-all mr-2',
                    isChecked ? 'border-success-main' : 'border-grey-200',
                    !isDisabled &&
                      !isChecked &&
                      'hover:border-grey-600 group-hover:border-grey-600',
                    isDisabled && 'border-grey-200',
                    !!error && ' border-error-main'
                  )}
                >
                  {isChecked && (
                    <CheckmarkIcon
                      className={cn('text-sm', isDisabled ? 'text-grey-200' : 'text-success-main')}
                    />
                  )}
                </div>

                <div className="text-grey-600">{option.label}</div>
              </label>
            );
          })}
        </Box>
        {helperText && (
          <div className={cn('mt-2 text-xs text-grey-600', error && 'text-error-main')}>
            {helperText}
          </div>
        )}
      </FormControl>
    </TooltipComponent>
  );
};
